import { useEffect, useRef, useState } from 'react';
import './App.css';
import AppRoutes from './components/AppRoutes';

import {
  buildAppInstallationInfo,
  configureLDUStates,
  FingerprintJSProvider,
  getAuthorizationHeader,
  IAppInstallationInfo,
  initializeAmplifyAuth,
} from '@brandclub/common-ui';
import { GetAppConfigFromServerResponse } from './api/rest/unauthenticated/AppConfig';
import StoreFullScreenLoading from './components/StoreComponents/StoreFullScreenLoading';
import { useAppDispatch, useAppSelector } from './redux/hooks';
import { getAppConfig, getBrandAppConfigs } from './redux/reducers/appConfig/thunk';
import axiosInstance from './utils/axios';
import ConfigUtils from './utils/ConfigUtils';
import FaviconManager from './components/FaviconManager';
import { queryClient } from './queryClient';
import { CachedQueryKeys } from '@brandclub/common-ui';

export const updateAppInstallationInfoInLandingPage = async (appInstallationInfo: {
  deviceInfo: any;
  appInfo: any;
}): Promise<any> => {
  const headers: Record<any, any> = {
    ...(await getAuthorizationHeader()),
  };
  const res = await axiosInstance.post('/appInstallation/update', appInstallationInfo, {
    headers,
  });

  const { status, payload } = res.data || {};
  return status === 'success' ? payload : undefined;
};

const App = () => {
  const authConfig = useAppSelector(({ appConfig }) => appConfig?.authConfig);
  const domainName = useAppSelector(({ appConfig }) => appConfig?.domainConfig?.domainName);
  const lduAffectedStates = useAppSelector(({ appConfig }) => appConfig?.domainConfig?.lduAffectedStates);
  const userId = useAppSelector(({ userProfile }) => userProfile?.userId);
  const [isAuthInitialized, setIsAuthInitialized] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const appConfigFetchInFlightRef = useRef(false);
  const authInitInFlightRef = useRef(false);

  useEffect(() => {
    (async () => {
      if (authConfig && domainName) {
        if (isAuthInitialized || authInitInFlightRef.current) {
          return;
        }
        authInitInFlightRef.current = true;
        try {
          await initializeAmplifyAuth(authConfig, domainName);
          setIsAuthInitialized(true);
        } finally {
          authInitInFlightRef.current = false;
        }

        try {
          if (userId) {
            const appId = ConfigUtils.getWebAppId();
            const appInstallationInfo: IAppInstallationInfo = await buildAppInstallationInfo({ userId, appId });
            if (appInstallationInfo) {
              await updateAppInstallationInfoInLandingPage(appInstallationInfo);
            }
          }
        } catch (e) {
          console.error('An error occurred when building appInstallationInfo: ', e);
        }
      } else {
        if (appConfigFetchInFlightRef.current) return;
        appConfigFetchInFlightRef.current = true;
        try {
          const payload = (await dispatch(getAppConfig())).payload as GetAppConfigFromServerResponse;
          // Seed React Query cache so common-ui consumers (ConciergeContainer, widgets) don't refetch appConfig.
          if (payload?.appConfig?.authConfig && payload?.appConfig?.domainConfig) {
            queryClient.setQueryData([CachedQueryKeys.AppConfig], {
              authConfig: payload.appConfig.authConfig,
              domainConfig: payload.appConfig.domainConfig,
            });
          }
          if (payload?.appConfig?.domainConfig) {
            // if there's no appConfig, get the brand app config as soon as we have the appConfig response to avoid race conditions
            dispatch(
              getBrandAppConfigs({
                appId: payload.appConfig.domainConfig.domainName,
              }),
            );
          }
        } finally {
          appConfigFetchInFlightRef.current = false;
        }
      }
    })();
  }, [authConfig, dispatch, userId, domainName, isAuthInitialized]);

  // Configure LDU affected states from app config (falls back to defaults if not provided)
  useEffect(() => {
    if (lduAffectedStates) {
      configureLDUStates(lduAffectedStates);
    }
  }, [lduAffectedStates]);

  return (
    <FingerprintJSProvider hostDomainName={`https://${window.location.hostname}`}>
      <FaviconManager />
      {!!authConfig && isAuthInitialized ? <AppRoutes /> : <StoreFullScreenLoading storeHeader={false} />}
    </FingerprintJSProvider>
  );
};

export default App;
